import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { GraduationCap, Mic, FileSearch, Briefcase, ArrowRight, Sparkles } from "lucide-react";
import api from "@/lib/api";
import DataNode from "./DataNode";
import { polarToPercent, polarToSvgPoint } from "./nexusGeometry";

const SKILL_STATUS_STYLE = {
  mastered: { node: "#34D399", line: "rgba(255,255,255,0.35)", dash: "0" },
  current: { node: "#5b9dff", line: "rgba(91,157,255,0.5)", dash: "0" },
  pending: { node: "rgba(255,255,255,0.15)", line: "rgba(255,255,255,0.08)", dash: "4 4" },
};

const DATA_ANGLES = [45, 135, 225, 315];
const SKILL_ANGLES = [0, 90, 180, 270];
const DATA_NODE_RADIUS = 30;
const SKILL_NODE_RADIUS = 47;

export default function NexusCore() {
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    api.get("/nexus/summary").then((res) => setSummary(res.data)).catch(() => {});
  }, []);

  if (!summary) {
    return <div className="neu-card rounded-2xl p-6 h-[420px] animate-pulse" data-testid="nexus-core-loading" />;
  }

  const dataNodes = [
    { icon: GraduationCap, color: "#5b9dff", label: "Challenge Progress", value: summary.challenge_progress_pct, suffix: "%", testId: "nexus-node-challenge" },
    { icon: Mic, color: "#b58bff", label: "Interview Score", value: summary.interview_avg_score, suffix: summary.interview_avg_score !== null ? "%" : "", testId: "nexus-node-interview" },
    { icon: FileSearch, color: "#ffb020", label: "Resume ATS Score", value: summary.resume_ats_score, suffix: summary.resume_ats_score ? "%" : "", testId: "nexus-node-ats" },
    { icon: Briefcase, color: "#34D399", label: "Best Job Match", value: summary.job_match_best_pct, suffix: summary.job_match_best_pct ? "%" : "", testId: "nexus-node-job-match" },
  ];

  const ringSpeed = Math.max(8, 26 - summary.consistency_score / 5); // more consistent learner = faster spin

  return (
    <motion.div
      className="neu-card rounded-2xl p-6 relative overflow-hidden" data-testid="nexus-core"
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
    >
      <div className="flex items-center justify-between mb-2 relative z-10">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5 text-[#5b9dff]" /> Career DNA
        </span>
        <span className="text-[10px] font-mono text-slate-500" data-testid="nexus-primary-track">{summary.primary_track_label} track</span>
      </div>

      <div className="relative w-full mx-auto" style={{ maxWidth: 440, height: 400 }} data-testid="nexus-galaxy-canvas">
        <svg viewBox="0 0 400 400" className="absolute inset-0 w-full h-full">
          {summary.skills.map((skill, i) => {
            const style = SKILL_STATUS_STYLE[skill.status];
            const p = polarToSvgPoint(SKILL_ANGLES[i], (SKILL_NODE_RADIUS / 50) * 200);
            return (
              <line key={skill.label} x1={200} y1={200} x2={p.x} y2={p.y} stroke={style.line} strokeWidth={1.5} strokeDasharray={style.dash} />
            );
          })}
          <motion.circle
            cx={200} cy={200} r={92} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={1} strokeDasharray="2 6"
            animate={{ rotate: 360 }} transition={{ duration: ringSpeed, repeat: Infinity, ease: "linear" }} style={{ originX: "200px", originY: "200px" }}
          />
          <motion.circle
            cx={200} cy={200} r={128} fill="none" stroke="rgba(181,139,255,0.08)" strokeWidth={1} strokeDasharray="1 10"
            animate={{ rotate: -360 }} transition={{ duration: ringSpeed * 1.6, repeat: Infinity, ease: "linear" }} style={{ originX: "200px", originY: "200px" }}
          />
        </svg>

        <motion.div
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 rounded-full flex items-center justify-center"
          style={{ background: "radial-gradient(circle at 35% 30%, #8bb8ff, #5b9dff 55%, #6d3fc9 100%)", boxShadow: "0 0 50px rgba(91,157,255,0.45), 0 0 90px rgba(181,139,255,0.25)" }}
          animate={{ scale: [1, 1.06, 1] }} transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          data-testid="nexus-core-orb"
        >
          <span className="font-mono text-sm font-bold text-white tabular-nums">{summary.challenge_progress_pct}%</span>
        </motion.div>

        {dataNodes.map((n, i) => <DataNode key={n.testId} angle={DATA_ANGLES[i]} radius={DATA_NODE_RADIUS} {...n} />)}

        {summary.skills.map((skill, i) => {
          const style = SKILL_STATUS_STYLE[skill.status];
          const pos = polarToPercent(SKILL_ANGLES[i], SKILL_NODE_RADIUS);
          return (
            <motion.div
              key={skill.label} className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-1 z-10" style={pos}
              data-testid={`nexus-skill-${skill.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.5 }}
              whileHover={{ scale: 1.1 }}
            >
              <motion.div
                className="w-4 h-4 rounded-full border-2"
                style={{ borderColor: style.node, background: skill.status !== "pending" ? style.node : "transparent" }}
                animate={skill.status === "current" ? { boxShadow: [`0 0 0px ${style.node}`, `0 0 14px ${style.node}`, `0 0 0px ${style.node}`] } : {}}
                transition={{ duration: 2, repeat: Infinity }}
              />
              <p className="text-[9px] font-semibold text-slate-300 text-center leading-tight max-w-[76px]">{skill.label}</p>
            </motion.div>
          );
        })}
      </div>

      <div className="flex items-center justify-between mt-2 pt-4 border-t border-white/5 relative z-10">
        <p className="text-xs text-slate-400" data-testid="nexus-suggested-action-label">{summary.suggested_action.label}</p>
        <Link to={summary.suggested_action.link} data-testid="nexus-suggested-action-link" className="flex items-center gap-1 text-xs font-semibold text-[#5b9dff] hover:text-[#8bb8ff]">
          Go <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
    </motion.div>
  );
}
